import * as Schema from "effect/Schema";
import { createStore } from "zustand/vanilla";
import {
  PersistedComposerImageAttachmentSchema,
  PersistedComposerFileAttachmentSchema,
  PersistedTerminalContextDraftSchema,
} from "./composerStagedSubmission";

const Entry = Schema.Struct({
  id: Schema.String,
  createdAt: Schema.String,
  prompt: Schema.String,
  attachments: Schema.Array(PersistedComposerImageAttachmentSchema),
  files: Schema.optionalKey(Schema.Array(PersistedComposerFileAttachmentSchema)),
  terminalContexts: Schema.Array(
    Schema.Struct({
      ...PersistedTerminalContextDraftSchema.fields,
      text: Schema.String,
    }),
  ),
});
export type PromptStashEntry = typeof Entry.Type;
const StoredStash = Schema.Struct({
  version: Schema.Literal(1),
  entries: Schema.Array(Entry),
});
const decode = Schema.decodeUnknownSync(StoredStash);
const MAX_STASH_ENTRIES = 20;
export const PROMPT_STASH_STORAGE_KEY = "forge:prompt-stash:v1";

/** Returns null when persisted stash data cannot prove every file reference shape. */
export function readPersistedPromptStashFileAttachmentIds(raw: string | null): Set<string> | null {
  if (raw === null) return new Set();
  try {
    const ids = new Set<string>();
    for (const entry of decode(JSON.parse(raw)).entries) {
      for (const file of entry.files ?? []) ids.add(file.id);
    }
    return ids;
  } catch {
    return null;
  }
}

export interface PromptStashStorage {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
  readonly subscribe?: (listener: () => void) => () => void;
}

type WriteResult = { readonly ok: true } | { readonly ok: false; readonly reason: "storage" };
interface PromptStashState {
  readonly entries: readonly PromptStashEntry[];
  readonly contains: (entry: PromptStashEntry) => boolean;
  readonly save: (
    entry: PromptStashEntry,
  ) => WriteResult & { readonly evicted?: PromptStashEntry | null };
  readonly remove: (id: string) => WriteResult;
}

function readEntries(storage: PromptStashStorage | null): readonly PromptStashEntry[] {
  try {
    const raw = storage?.getItem(PROMPT_STASH_STORAGE_KEY);
    return raw ? decode(JSON.parse(raw)).entries : [];
  } catch {
    return [];
  }
}

function hasStoredEntries(
  storage: PromptStashStorage,
  expected: readonly PromptStashEntry[],
): boolean {
  try {
    const raw = storage.getItem(PROMPT_STASH_STORAGE_KEY);
    if (!raw) return expected.length === 0;
    return JSON.stringify(decode(JSON.parse(raw)).entries) === JSON.stringify(expected);
  } catch {
    return false;
  }
}

export function createPromptStashStore(storage: PromptStashStorage | null) {
  const initial = readEntries(storage);
  const store = createStore<PromptStashState>()((set) => {
    const write = (entries: readonly PromptStashEntry[]): WriteResult => {
      if (!storage) return { ok: false, reason: "storage" };
      try {
        storage.setItem(PROMPT_STASH_STORAGE_KEY, JSON.stringify({ version: 1, entries }));
      } catch {
        return { ok: false, reason: "storage" };
      }
      if (!hasStoredEntries(storage, entries)) {
        return { ok: false, reason: "storage" };
      }
      set({ entries });
      return { ok: true };
    };
    return {
      entries: initial,
      contains: (entry) =>
        readEntries(storage).some((stored) => JSON.stringify(stored) === JSON.stringify(entry)),
      save: (entry) => {
        const current = readEntries(storage);
        const next = [entry, ...current.filter((saved) => saved.id !== entry.id)];
        const evicted = next.length > MAX_STASH_ENTRIES ? (next.pop() ?? null) : null;
        const result = write(next);
        return result.ok ? { ...result, evicted } : result;
      },
      remove: (id) => write(readEntries(storage).filter((entry) => entry.id !== id)),
    };
  });
  storage?.subscribe?.(() => store.setState({ entries: readEntries(storage) }));
  return store;
}

const browserStorage: PromptStashStorage | null =
  typeof window === "undefined"
    ? null
    : {
        getItem: (key) => localStorage.getItem(key),
        setItem: (key, value) => localStorage.setItem(key, value),
        subscribe: (listener) => {
          const refresh = (event: StorageEvent) => {
            if (event.key === PROMPT_STASH_STORAGE_KEY || event.key === null) listener();
          };
          window.addEventListener("storage", refresh);
          return () => window.removeEventListener("storage", refresh);
        },
      };

/** One stash collection shared by the landing and thread composers. */
export const promptStashStore = createPromptStashStore(browserStorage);
